<template>
  <div class="ez-bill-page">
    <!-- Page Header with Title -->
    <div class="page-header py-6 px-10">
      <div class="page-title-section">
        <h1 class="page-title">eZ-Bill Products</h1>
        <p class="page-subtitle">Manage your billing products and fees in one place</p>
      </div>
    </div>

    <!-- Global Search Bar -->
    <div class="px-10 pb-4">
      <div class="search-container">
        <div class="search-label">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#374957"/>
          </svg>
          <span class="search-label-text">Search Through eZ-Bill</span>
        </div>
        <div class="search-input-wrapper">
          <TextInput
            v-model="searchQuery"
            size="medium"
            :placeholder="searchPlaceholder"
            :clearable="true"
          />
        </div>
      </div>
    </div>

    <div class="px-10 mb-[2px]">
      <Tab
        v-model="activeTab"
        type="outlined"
        :tabs="mainTabs"
        class="ez-bill-main-tab"
      />
    </div>

    <!-- Content Area with White Background -->
    <div class="content-area -mr-5 -ml-5 px-10">
      <!-- Signature Programs Content -->
      <div v-if="activeTab === 'signature-programs'" class="programs-content">
        <ProgramsTable
          @add-program="handleAddProgram"
          @edit-price-batch="handleEditPriceBatch"
          @view="handleViewProgram"
          @edit-price="handleEditProgramPrice"
          @archive="handleArchiveProgram"
        />
      </div>

      <!-- Vibrant Products Content -->
      <div v-else-if="activeTab === 'vibrant-products'" class="vibrant-products-content">
        <Loading :loading="loading" loadingText="Loading...">
        <div class="products-content-wrapper" :class="{ 'loading': loading }">
        <!-- Filter Row -->
        <div class="filter-row">
          <div class="filter-item">
            <label class="filter-label">Sort By</label>
            <SingleSelector
              v-model="filterSortBy"
              size="medium"
              placeholder="Select"
              :options="sortByOptions"
            />
          </div>
          <div class="filter-item">
            <label class="filter-label">Type</label>
            <SingleSelector
              v-model="filterType"
              size="medium"
              placeholder="Select"
              :options="typeOptions"
            />
          </div>
          <div class="filter-item">
            <label class="filter-label">Customize</label>
            <SingleSelector
              v-model="filterCustomize"
              size="medium"
              placeholder="Select"
              :options="customizeOptions"
            />
          </div>
          <div v-if="providerOptions.length > 0" class="filter-item">
            <label class="filter-label">Choose Provider</label>
            <SingleSelector
              v-model="filterProvider"
              size="medium"
              placeholder="Select"
              :options="providerOptions"
            />
          </div>
          <ResponsiveButton
            class="batch-edit-btn-wrapper"
            button_type="filled"
            built_in_theme="dark"
            size="medium"
            display_name="Edit Patient Price By Batch"
            :disabled="selectedCount === 0"
            @click-button="handleBatchEdit"
          >
            <template #prefix>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M8 17H10V16H11C11.55 16 12 15.55 12 15V12C12 11.45 11.55 11 11 11H8V10H12V8H10V7H8V8H7C6.45 8 6 8.45 6 9V12C6 12.55 6.45 13 7 13H10V14H6V16H8V17ZM20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V6H20V18ZM14 10L16 8L18 10H14ZM18 14.25L16 16.25L14 14.25" fill="white"/>
              </svg>
            </template>
          </ResponsiveButton>
        </div>

        <!-- Sub Tabs -->
        <Tab
          v-model="activeSubTab"
          type="underline"
          :tabs="subTabs"
          class="ez-bill-sub-tab"
        />

        <!-- Table -->
        <div class="products-table-wrapper">
          <div class="products-table">
          <!-- Table Header -->
          <div class="table-header">
            <div class="th checkbox">
              <Checkbox
                :model-value="allSelected"
                built_in_theme="primary-blue"
                @change="toggleSelectAll"
              />
            </div>
            <div class="th test-name">TEST NAME</div>
            <div class="th price">BASE PRICE</div>
            <div class="th markup">MARKUP%</div>
            <div class="th price">PATIENT PRICE</div>
            <div class="th revenue">REVENUE</div>
            <div class="th actions">ACTIONS</div>
          </div>

          <!-- Table Body -->
          <div class="table-body">
            <!-- No data state -->
            <div v-if="!loading && displayItems.length === 0" class="no-data-state">
              <p>No results found.</p>
              <p>We couldn't find what you searched for. Try searching again.</p>
            </div>

            <!-- Data rows -->
            <div
              v-for="item in displayItems"
              :key="item.id"
              class="table-row clickable"
              @click="toggleItemSelection(item.id)"
            >
              <div class="td checkbox" @click.stop>
                <Checkbox
                  :model-value="item.selected"
                  built_in_theme="primary-blue"
                  @change="toggleItemSelection(item.id, $event)"
                />
              </div>
              <div class="td test-name">{{ item.name }}</div>
              <div class="td price">{{ currencySymbol }}{{ item.basePrice.toFixed(2) }}</div>
              <div class="td markup">{{ item.markup }}</div>
              <div class="td price">{{ currencySymbol }}{{ item.retailPrice.toFixed(2) }}</div>
              <div class="td revenue">{{ currencySymbol }}{{ item.revenue.toFixed(2) }}</div>
              <div class="td actions" @click.stop>
                <button class="edit-btn" @click.stop="handleEdit(item)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M21.4555 5.41575C21.6477 5.70687 21.6157 6.10248 21.3594 6.35876L12.167 15.5511C12.0728 15.6454 11.9552 15.7128 11.8263 15.7465L7.99783 16.7465C7.87296 16.7791 7.74425 16.7784 7.62332 16.7476C7.49475 16.7149 7.37498 16.6482 7.27797 16.5511C7.08969 16.3629 7.01535 16.0889 7.08264 15.8313L8.08264 12.0028C8.11211 11.89 8.1674 11.7786 8.24389 11.6912L17.4703 2.46967C17.5511 2.38891 17.6484 2.32846 17.7543 2.29163C17.8328 2.26432 17.916 2.25 18.0007 2.25C18.1996 2.25 18.3903 2.32902 18.531 2.46967L21.3594 5.2981C21.396 5.33471 21.4281 5.37416 21.4555 5.41575ZM19.7684 5.82843L18.0007 4.06066L9.48251 12.5788L8.85752 14.9716L11.2503 14.3466L19.7684 5.82843Z" fill="#374957"/>
                    <path d="M19.642 17.1603C19.9154 14.8227 20.0024 12.4688 19.903 10.1208C19.8982 10.0084 19.9405 9.89898 20.0201 9.81942L21.0034 8.83609C21.1243 8.71519 21.3309 8.79194 21.3422 8.96254C21.5271 11.7522 21.457 14.5545 21.1319 17.3346C20.8953 19.3571 19.271 20.9421 17.2589 21.167C13.7924 21.5544 10.209 21.5544 6.74244 21.167C4.73038 20.9421 3.10605 19.3571 2.8695 17.3346C2.45496 13.7903 2.45496 10.2097 2.8695 6.66543C3.10605 4.6429 4.73038 3.05789 6.74244 2.83301C9.37219 2.5391 12.0691 2.46815 14.7313 2.62016C14.9029 2.62996 14.9811 2.83757 14.8596 2.95909L13.867 3.95165C13.7883 4.03034 13.6804 4.07261 13.5692 4.06885C11.3428 3.99376 9.10122 4.07872 6.90905 4.32373C5.57894 4.47239 4.51346 5.522 4.35934 6.83968C3.95834 10.2682 3.95834 13.7318 4.35934 17.1603C4.51346 18.478 5.57894 19.5276 6.90905 19.6763C10.2649 20.0513 13.7365 20.0513 17.0923 19.6763C18.4224 19.5276 19.4879 18.478 19.642 17.1603Z" fill="#374957"/>
                  </svg>
                </button>
                <!-- Delete button hidden until delete API is available
                <button class="delete-btn" @click.stop="handleDelete(item)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="#D10000"/>
                  </svg>
                </button>
                -->
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <Pagination
            class="ez-bill-pagination"
            :total_items="totalCount"
            :current_page="currentPage"
            :per_page="perPage"
            :per_page_options="[5, 10, 15]"
            @update:current_page="handlePageChange"
            @update:per_page="handlePageSizeChange"
          />
          </div>
        </div>
        </div> <!-- End products-content-wrapper -->
        </Loading>
      </div>

      <!-- Practice Products Tab -->
      <div v-else-if="activeTab === 'practice-products'" class="practice-products-tab">
        <PracticeProducts
          :provider-options="providerOptions"
          :currency-symbol="currencySymbol"
          :is-clinic="isClinic"
          :search-query="searchQuery"
          :user-info="userInfo"
        />
      </div>

      <!-- Customized Fee Tab -->
      <div v-else-if="activeTab === 'customized-fee'">
        <CustomizedFee @edit-fee="handleEditFee" />
      </div>

      <!-- Product Bundles Tab -->
      <div v-else-if="activeTab === 'product-bundles'">
        <ProductBundles
          :currency-symbol="currencySymbol"
          :provider-options="providerOptions"
          :search-query="searchQuery"
          :filter-provider="filterProvider"
          @edit-bundle="handleEditBundle"
        />
      </div>

      <!-- Other Tabs - Coming Soon -->
      <div v-else class="content-body">
        <div class="coming-soon-state">
          <p>{{ activeTabDisplay }} coming soon...</p>
        </div>
      </div>
    </div>

    <!-- Batch Edit Dialog -->
    <BatchEditDialog
      v-model="batchEditDialogVisible"
      :selected-products="selectedItems"
      :provider-options="providerOptions"
      :current-provider-id="filterProvider"
      :is-clinic="isClinic"
      :clinic-id="userInfo?.clinic_id"
      :customer-id="userInfo?.customer_id"
      :currency-symbol="currencySymbol"
      @update-table-rows="handleBatchUpdateTableRows"
    />

    <!-- Edit Retail Details Dialog -->
    <EditRetailDetailsDialog
      v-model="editDialogVisible"
      :selected-product="editingItem"
      :provider-options="providerOptions"
      :current-provider-id="filterProvider"
      :is-clinic="isClinic"
      :clinic-id="userInfo?.clinic_id"
      :customer-id="userInfo?.customer_id"
      :currency-symbol="currencySymbol"
      @update-table-row="handleUpdateTableRow"
    />
  </div>
</template>

<script>
import {
  getPromotions,
  getCustomPanels,
  getMarkUps,
  getJwtTokenCustomerList
} from '@/api/markup';
import { getSettingData } from '@/api/setting';
import { formatData2 } from '@/utils/ezBillFormatter';
import { notify } from '@/utils/notify';
import { getStateAbbreviation } from '@/utils/va-order-test/parsePatient';
import CustomizedFee from './components/CustomizedFee.vue';
import EditRetailDetailsDialog from './components/EditRetailDetailsDialog.vue';
import BatchEditDialog from './components/BatchEditDialog.vue';
import PracticeProducts from './components/PracticeProducts.vue';
import ProductBundles from './components/ProductBundles.vue';
import ProgramsTable from './components/ProgramsTable.vue';
import Loading from '@/NewLibrary/Loading/Loading.vue';
import AllIcon from '@/assets/MyWorkBench/ez-bill-sub-tab/all.svg';
import VibrantAmericaIcon from '@/assets/MyWorkBench/ez-bill-sub-tab/vibrant-america.svg';
import VibrantWellnessIcon from '@/assets/MyWorkBench/ez-bill-sub-tab/vibrant-wellness.svg';
import VibrantHighlightsIcon from '@/assets/MyWorkBench/ez-bill-sub-tab/vibrant-highlights.svg';
import CustomPanelIcon from '@/assets/MyWorkBench/ez-bill-sub-tab/custom-panel.svg';
import VibrantProductsTabIcon from '@/assets/MyWorkBench/ez-bill-main-tab/vibrant-products.svg';
import SignatureProgramsTabIcon from '@/assets/MyWorkBench/ez-bill-main-tab/signature-programs.svg';
import PracticeProductsTabIcon from '@/assets/MyWorkBench/ez-bill-main-tab/practice-products.svg';
import ProductBundlesTabIcon from '@/assets/MyWorkBench/ez-bill-main-tab/product-bundles.svg';
import CustomizedFeeTabIcon from '@/assets/MyWorkBench/ez-bill-main-tab/customized-fee.svg';

export default {
  name: 'EZBill',
  components: {
    CustomizedFee,
    EditRetailDetailsDialog,
    BatchEditDialog,
    PracticeProducts,
    ProductBundles,
    ProgramsTable,
    Loading,
  },

  data() {
    return {
      activeTab: 'vibrant-products',
      searchQuery: '',
      searchOption: 'test', // 'test' or 'category'

      // Vibrant Products state
      activeSubTab: 'all',
      filterSortBy: 'default',
      filterType: 'all',
      filterCustomize: 'all',
      filterProvider: '', // Will be set to first provider (empty string instead of null)
      perPage: 10,
      currentPage: 1,

      // Filter options
      sortByOptions: [
        { label: 'Recommendation', value: 'default' },
        // { label: 'Test Name A-Z', value: 'nameAZ' },
        // { label: 'Test Name Z-A', value: 'nameZA' },
        // { label: 'Revenue Highest to Lowest', value: 'profit_descending' },
        // { label: 'Revenue Lowest to Highest', value: 'profit_ascending' },
        { label: 'Name A-Z', value: 'nameAZ' },
        { label: 'Name Z-A', value: 'nameZA' },
        { label: 'Price Highest to Lowest', value: 'price_descending' },
        { label: 'Price Lowest to Highest', value: 'price_ascending' },
      ],
      typeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Test', value: 'test' },
        { label: 'Non-Test', value: 'non-test' },
      ],
      customizeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Customized Only', value: 'customized' },
        { label: 'Not Customized Only', value: 'not_customized' },
      ],
      searchOptions: [
        { label: 'Test Name', value: 'test' },
        { label: 'Category Name', value: 'category' },
      ],
      providerOptions: [], // Will be populated from API, no "All" option (same as Portal)
      perPageOptions: [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '15', value: 15 },
      ],

      // Sub tabs (5 tabs - removed Discount Panel and Favorite Shortcut) — icons via tab.icon prop
      subTabs: [
        { value: 'all', label: 'All', icon: AllIcon },
        { value: 'va', label: 'Vibrant America', icon: VibrantAmericaIcon },
        { value: 'vw', label: 'Vibrant Wellness', icon: VibrantWellnessIcon },
        { value: 'promotion', label: 'Vibrant Highlights', icon: VibrantHighlightsIcon },
        { value: 'custom', label: 'Custom Panel', icon: CustomPanelIcon },
      ],

      // Real data from API
      productsData: [], // All products from API
      loading: true, // Start loading immediately (stops when fetchProductsData completes)

      // Cached data for provider switching (same logic as Portal)
      // Markup schemes and test data are shared across providers
      cachedMarkupSchemes: {}, // All markup schemes keyed by affiliate_id
      cachedTestsData: null, // Tests data sourced from orderTest/tests Vuex store
      cachedPromotionsData: null,

      // Custom panels are per-provider (lazy loaded)
      cachedCustomPanelsByProvider: {}, // Key: provider_id, Value: customPanels array

      isDataCached: false, // Flag to indicate if initial data is loaded

      // Edit dialog state
      editDialogVisible: false,
      editingItem: null,

      // Batch edit dialog state
      batchEditDialogVisible: false,

    };
  },

  computed: {
    pageTitle() {
      return 'eZ-Bill Products';
    },

    pageSubtitle() {
      return 'Manage your billing products and fees in one place';
    },

    activeTabDisplay() {
      const displayNames = {
        'signature-programs': 'Signature Programs',
        'vibrant-products': 'Vibrant Products',
        'practice-products': 'Practice Products',
        'product-bundles': 'Product Bundles',
        'customized-fee': 'Customized Fees'
      };
      return displayNames[this.activeTab] || 'Items';
    },

    searchPlaceholder() {
      return `Search ${this.activeTabDisplay}`;
    },

    createButtonText() {
      const buttons = {
        'signature-programs': 'Create Program',
        'vibrant-products': 'Add Product',
        'practice-products': 'Add Product',
        'product-bundles': 'Create Bundle',
        'customized-fee': 'Save Settings'
      };
      return buttons[this.activeTab] || 'Create';
    },

    emptySubtitle() {
      const subtitles = {
        'signature-programs': 'Create your first signature program',
        'vibrant-products': 'Add your first Vibrant product',
        'practice-products': 'Add your first practice product',
        'product-bundles': 'Create your first product bundle',
        'customized-fee': 'Configure your preferences'
      };
      return subtitles[this.activeTab] || '';
    },

    // Debug: access userInfo from store
    userInfo() {
      return this.$store?.state?.login?.userInfo;
    },

    // Mirror Portal's ResIndex.vue showMarkupPage: hide Vibrant Products main tab
    // for US users in NY/NJ/RI/CA. Non-US users are not restricted.
    showMarkupPage() {
      if (!this.userInfo) {
        return true;
      }
      const { customer_country, country, customer_state, state } = this.userInfo;
      const local_country = (customer_country || country || '').toLowerCase();
      const isUS = ['us', 'usa', 'united states'].includes(local_country);
      if (local_country && !isUS) {
        return true;
      }
      const raw_state = customer_state ? customer_state : state ? state : '';
      const local_state = raw_state ? getStateAbbreviation(raw_state) : '';
      const restrictedStates = ['NY', 'NJ', 'RI', 'CA'];
      if (restrictedStates.includes(local_state.toUpperCase())) {
        return false;
      }
      return true;
    },

    // Main tabs — icons via tab.icon prop. SVG fill is #666 (unselected color);
    // the selected tab color is applied via CSS filter (see .ez-bill-main-tab below).
    mainTabs() {
      const tabs = [];
      if (this.showMarkupPage) {
        tabs.push({ value: 'vibrant-products', label: 'Vibrant Products', icon: VibrantProductsTabIcon });
      }
      tabs.push(
        { value: 'signature-programs', label: 'Signature Programs', icon: SignatureProgramsTabIcon },
        { value: 'practice-products', label: 'Practice Products', icon: PracticeProductsTabIcon },
        { value: 'product-bundles', label: 'Product Bundles', icon: ProductBundlesTabIcon },
        { value: 'customized-fee', label: 'Customized Fee', icon: CustomizedFeeTabIcon },
      );
      return tabs;
    },

    // Currency symbol from Vuex store (global currency management)
    currencySymbol() {
      return this.$store?.getters['currency/currencySymbol'] || '$';
    },

    // Check if user is clinic role (same logic as VA Portal)
    isClinic() {
      const userRoles = this.userInfo?.user_roles || [];
      return Array.isArray(userRoles) && userRoles.includes('clinic');
    },

    // Filtered and sorted data based on current filters (same logic as Portal)
    filteredItems() {
      const dataSource = this.productsData || [];

      // Filter parameters (same as Portal)
      const searchInput = this.searchQuery;
      const searchOption = this.searchOption || 'test';
      const customizedValue = this.filterCustomize;
      const isTestValue = this.filterType;
      const sortByValue = this.filterSortBy;

      // Helper function to filter (same as Portal's filterData)
      const filterByType = (array) => {
        let filtered = [...array];

        // 1. Filter by search query (test name or category)
        if (searchInput && searchInput.trim()) {
          const query = searchInput.toLowerCase().trim();
          const field = searchOption === 'category' ? 'category' : 'name';
          filtered = filtered.filter(item =>
            item[field] && item[field].toLowerCase().includes(query)
          );
        }

        // 2. Filter by customization status
        if (customizedValue && customizedValue !== 'all') {
          filtered = filtered.filter(row => {
            if (customizedValue === 'not_customized') {
              return row.markUpType === 'no_markup';
            } else {
              return row.markUpType !== 'no_markup';
            }
          });
        }

        // 3. Filter by test type
        if (isTestValue !== 'all') {
          const bool = isTestValue === 'test';
          filtered = filtered.filter(row => row.isTest === bool);
        }

        return filtered;
      };

      // Helper function to sort (same as Portal's sortData)
      const sortBy = (array) => {
        const sorted = [...array];
        const typeOrder = ['va', 'vw', 'promotion', 'discount', 'custom'];

        sorted.sort((a, b) => {
          if (sortByValue === 'nameAZ') {
            return a.name.localeCompare(b.name);
          } else if (sortByValue === 'nameZA') {
            return b.name.localeCompare(a.name);
          // } else if (sortByValue === 'profit_descending') {
          //   return (b.profit || b.revenue || 0) - (a.profit || a.revenue || 0);
          // } else if (sortByValue === 'profit_ascending') {
          //   return (a.profit || a.revenue || 0) - (b.profit || b.revenue || 0);
          } else if (sortByValue === 'price_descending') {
            return (b.retailPrice || 0) - (a.retailPrice || 0);
          } else if (sortByValue === 'price_ascending') {
            return (a.retailPrice || 0) - (b.retailPrice || 0);
          } else if (sortByValue === 'default') {
            const typeComparison = typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
            if (typeComparison !== 0) {
              return typeComparison;
            }
            return (a.index ?? 0) - (b.index ?? 0);
          }
          return 0;
        });

        return sorted;
      };

      // Separate data by type (5 tabs - discount merged into Vibrant Highlights)
      const va = dataSource.filter(item => item.type === 'va');
      const vw = dataSource.filter(item => item.type === 'vw');
      const promotion = dataSource.filter(item => item.type === 'promotion' || item.type === 'discount');
      const custom = dataSource.filter(item => item.type === 'custom');

      // Filter each type
      const vaFiltered = sortBy(filterByType(va));
      const vwFiltered = sortBy(filterByType(vw));
      const promotionFiltered = sortBy(filterByType(promotion));
      const customFiltered = sortBy(filterByType(custom));

      // Combine all for "all" tab (order: va + vw + promotion/discount + custom)
      const all = [...vaFiltered, ...vwFiltered, ...promotionFiltered, ...customFiltered];

      // Return data based on activeSubTab (5 tabs)
      switch (this.activeSubTab) {
        case 'all':
          return all;
        case 'va':
          return vaFiltered;
        case 'vw':
          return vwFiltered;
        case 'promotion':
          return promotionFiltered;
        case 'custom':
          return customFiltered;
        default:
          return all;
      }
    },

    // Paginated display items
    displayItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    // Total count for pagination
    totalCount() {
      return this.filteredItems.length;
    },

    allSelected() {
      const currentDisplay = this.displayItems;
      return currentDisplay.length > 0 &&
        currentDisplay.every(item => item.selected);
    },

    selectedCount() {
      return this.productsData.filter(item => item.selected).length;
    },

    selectedItems() {
      return this.productsData.filter(item => item.selected);
    },

  },

  watch: {
    // Reset to page 1 when filters change
    searchQuery() {
      this.currentPage = 1;
    },
    activeSubTab() {
      this.currentPage = 1;
    },
    filterSortBy() {
      this.currentPage = 1;
    },
    async filterProvider() {
      console.log('Provider changed to:', this.filterProvider);
      this.currentPage = 1;
      // Don't refetch - just re-filter from cached markup data (same as Portal)
      // This will lazy-load shortcuts/customPanels for the selected provider if needed
      await this.updateProductsDataFromCachedMarkup();
    },
    // Watch for userInfo to become available
    'userInfo': {
      handler(newVal) {
        console.log('userInfo changed:', newVal);
        // Handle nested value structure (some parts of EHR use nested value)
        const userInfoData = newVal?.value || newVal;
        if (userInfoData && userInfoData.customer_id && userInfoData.clinic_id) {
          console.log('userInfo now has customer_id and clinic_id, fetching products');
          this.fetchProductsData();
        }
      },
      immediate: true
    },
    // If the active main tab gets hidden (e.g. restricted-state user lands on
    // vibrant-products), fall back to the first visible tab.
    mainTabs: {
      handler(tabs) {
        if (tabs.length && !tabs.some(t => t.value === this.activeTab)) {
          this.activeTab = tabs[0].value;
        }
      },
      immediate: true,
    },
  },

  mounted() {
    console.log('ez-bill mounted, userInfo:', this.userInfo);
    // Set currency from billing settings (same as VA Portal's setCurrency in markup/index.vue)
    this.$store.dispatch('currency/setCurrency');
    // Don't fetch immediately - wait for userInfo watcher to trigger
  },

  methods: {
    async fetchProductsData() {
      this.loading = true;
      try {
        // Get user info from store for API params
        const userInfo = this.$store?.state?.login?.userInfo;

        if (!userInfo) {
          console.warn('No userInfo available, skipping API call');
          this.productsData = [];
          return;
        }

        const clinicId = userInfo.clinic_id;
        // Use token's customer_id (not filterProvider, for getMarkUps API call)
        const customerId = userInfo.customer_id;

        if (!clinicId || !customerId) {
          console.warn('Missing clinic_id or customer_id');
          this.productsData = [];
          return;
        }

        // Determine account type from user_roles
        const userRoles = userInfo.user_roles || [];
        const isClinic = Array.isArray(userRoles) && userRoles.includes('clinic');
        const accountType = isClinic ? 'clinic' : 'customer';

        // Fetch provider list if user is clinic
        // Also get default_provider from practice settings (same as VA Portal)
        let defaultProviderId = null;
        if (isClinic) {
          try {
            // Get default_provider from practice settings (same as VA Portal)
            const practiceInfoResp = await getSettingData('/setting/getPracticeInfo');
            if (practiceInfoResp?.data?.default_customer) {
              defaultProviderId = String(practiceInfoResp.data.default_customer);
            }

            const providersResponse = await getJwtTokenCustomerList();

            if (providersResponse.data) {
              // Format provider data: API returns [{ "25844": "Kris Legaspi" }, { "30450": "Xiaoye DT1" }]
              const providerList = [];
              providersResponse.data.forEach(item => {
                const [id, name] = Object.entries(item)[0];
                // Filter out closed clinics
                if (!name.toLowerCase().includes('(c)')) {
                  providerList.push({ id: Number(id), name, role: 'provider' });
                }
              });

              // Update provider options - NO "All" option (same as Portal)
              this.providerOptions = providerList.map(p => ({ label: p.name, value: String(p.id) }));

              // Set default to default_provider from API (same as VA Portal), fallback to first provider
              if (!this.filterProvider) {
                if (defaultProviderId && this.providerOptions.some(p => p.value === defaultProviderId)) {
                  this.filterProvider = defaultProviderId;
                } else if (this.providerOptions.length > 0) {
                  this.filterProvider = this.providerOptions[0].value;
                }
              }
            }
          } catch (error) {
            console.error('Failed to fetch providers:', error);
          }
        } else {
          // Customer user - no provider options needed
          this.providerOptions = [];
          this.filterProvider = String(customerId);
        }

        // Get currency from localStorage
        const currencyObj = JSON.parse(localStorage.getItem('currency') || '{"name":"USD"}');
        const currency = currencyObj.name?.toLowerCase() || 'usd';

        // Ensure user info is in the orderTest store (needed for setAllTests role/beta filtering)
        this.$store.commit('orderTest/user/SET_USER_INFO', userInfo);

        // Load tests into Vuex store only once — test menus are constant across provider switches
        if (!this.$store.state.orderTest.tests.vaBfsList.length) {
          await this.$store.dispatch('orderTest/tests/setAllTests');
        }

        // Call each API
        const promotionsResponse = await getPromotions(currency);

        // For getCustomPanels: use filterProvider for clinic users (same as VA Portal uses selectedAffId)
        // VA Portal logic: customerId = role === "customer" ? userInfo.customer_id : selectedAffId
        const customPanelsCustomerId = isClinic ? this.filterProvider : customerId;
        const customPanelsResponse = await getCustomPanels(currency, String(clinicId), String(customPanelsCustomerId), accountType);

        // Determine affiliate_ids based on selected provider (same logic as VA Portal)
        let affiliateIds;
        if (isClinic && this.providerOptions.length > 0) {
          // Clinic user - get all provider IDs from providerOptions
          // IMPORTANT: Do NOT add customer_id to affiliate_ids (use only providers from API)
          affiliateIds = this.providerOptions.map(p => Number(p.value));
        } else {
          // Customer user - only themselves
          affiliateIds = [Number(customerId)];
        }

        const markupResponse = await getMarkUps({
          account_type: accountType,
          account_id: isClinic ? Number(clinicId) : Number(customerId),
          affiliate_ids: affiliateIds,
          customer_id: Number(customerId),  // Use token's customer_id (same as VA Portal)
          clinic_id: Number(clinicId)
        });

        // Extract data from responses
        const promotionsData = promotionsResponse.data || [];
        const customPanelsData = customPanelsResponse.data || [];
        const markupData = markupResponse.data || {};

        // Cache all data for provider switching (same logic as Portal)
        this.cachedTestsData = {
          vaTests: this.$store.getters['orderTest/tests/vaTestList'],
          vwTests: this.$store.getters['orderTest/tests/vwTestList'],
        };
        this.cachedPromotionsData = promotionsData;
        this.cachedMarkupSchemes = markupData.markup_schemes || {};
        this.isDataCached = true;

        // Cache custom panels per provider (for lazy loading)
        // Use the same customerId that was used for the API call
        this.cachedCustomPanelsByProvider[String(customPanelsCustomerId)] = customPanelsData;

        // Don't filter providers - show all providers regardless of markup data

        // Update productsData from cached markup based on selected provider
        this.updateProductsDataFromCachedMarkup();

      } catch (error) {
        console.error('Failed to fetch products data:', error);
        this.productsData = [];
      } finally {
        this.loading = false;
      }
    },

    // Helper function to format markup display
    getMarkupDisplay(item) {
      // For discount bundles with negative profit, show "-"
      if (item.type === 'discount' || (item.profit !== undefined && item.profit < 0)) {
        return '-';
      }
      switch (item.markUpType) {
        case 'added_percentage':
          return `+${(item.markUpValue * 100).toFixed(0)}%`;
        case 'fixed_profit':
          return `+$${item.markUpValue.toFixed(2)}`;
        case 'fixed_price':
          return `$${item.markUpValue.toFixed(2)}`;
        default:
          return '+0%';
      }
    },

    // Update productsData from cached markup based on selected provider (same logic as Portal)
    async updateProductsDataFromCachedMarkup() {
      if (!this.isDataCached || !this.cachedMarkupSchemes) {
        console.warn('No cached data available');
        return;
      }

      const userInfo = this.$store?.state?.login?.userInfo;
      if (!userInfo) return;

      const customerId = userInfo.customer_id;
      const clinicId = userInfo.clinic_id;
      const userRoles = userInfo.user_roles || [];
      const isClinic = Array.isArray(userRoles) && userRoles.includes('clinic');

      // Determine which affiliate's data to use (same logic as Portal)
      // filterProvider is always a specific provider ID (no 'all' option)
      const selectedAffiliateId = String(this.filterProvider || customerId);

      const accountType = isClinic ? 'clinic' : 'customer';
      const currencyObj = JSON.parse(localStorage.getItem('currency') || '{"name":"USD"}');
      const currency = currencyObj.name?.toLowerCase() || 'usd';

      // Get markup schemes for selected affiliate - NO FALLBACK!
      // If provider has no markup, use empty array (shows 0 markup)
      const markupSchemes = this.cachedMarkupSchemes[selectedAffiliateId] || [];

      // Get custom panels for selected provider (lazy loading like Portal)
      let customPanelsData = this.cachedCustomPanelsByProvider[selectedAffiliateId];

      // Lazy load custom panels if not cached (same logic as Portal's setCustomPanelUnderClinic)
      if (!customPanelsData) {
        try {
          const customPanelsResponse = await getCustomPanels(currency, String(clinicId), selectedAffiliateId, accountType);
          customPanelsData = customPanelsResponse.data || [];
          this.cachedCustomPanelsByProvider[selectedAffiliateId] = customPanelsData;
        } catch (error) {
          console.error('Failed to load custom panels for provider:', selectedAffiliateId, error);
          customPanelsData = [];
        }
      }

      // Get discount panels from Vuex store (populated by setAllTests)
      const discountPanelsData = this.$store.state.orderTest?.panels?.discountPanelList || [];

      // VP-16301: pass vwCategories so VW list aligns with the Order page
      const vwCategoriesData = this.$store.state.orderTest?.tests?.vwCategories || [];

      // Format all data using formatData2 (includes discount panels in Vibrant Highlights)
      const { vaData, vwData, promotionData, discountData, customData } = formatData2(
        this.cachedTestsData?.vaTests || [],
        this.cachedTestsData?.vwTests || [],
        this.cachedPromotionsData || [],
        customPanelsData || [],
        markupSchemes,
        discountPanelsData,
        vwCategoriesData
      );

      // Helper to map formatted item for table display
      const mapItem = (item) => ({
        ...item,
        basePrice: item.wholeSalePrice,
        markup: this.getMarkupDisplay(item),
        retailPrice: item.markedUpPrice,
        revenue: item.profit
      });

      // Combine all data into productsData (order: va + vw + promotion + discount + custom)
      this.productsData = [
        ...vaData.map(mapItem),
        ...vwData.map(mapItem),
        ...promotionData.map(mapItem),
        ...discountData.map(mapItem),
        ...customData.map(mapItem)
      ];

      // Reset selection state (don't overwrite index - it's used for default sorting)
      this.productsData.forEach((item) => {
        item.selected = false;
      });
    },

    toggleSelectAll(checked) {
      const newState = !!checked;
      this.displayItems.forEach(item => {
        item.selected = newState;
      });
      // Sync selection back to productsData
      this.displayItems.forEach(displayItem => {
        const sourceItem = this.productsData.find(i => i.id === displayItem.id);
        if (sourceItem) {
          sourceItem.selected = displayItem.selected;
        }
      });
    },

    toggleItemSelection(id, checked) {
      const item = this.productsData.find(i => i.id === id);
      if (item) {
        if (typeof checked === 'boolean') {
          item.selected = checked;
        } else {
          item.selected = !item.selected;
        }
      }
    },

    handleEdit(item) {
      this.editingItem = { ...item };
      this.editDialogVisible = true;
    },

    handleBatchEdit() {
      if (this.selectedCount === 0) return;
      // VA Portal: non-clinic blocks if any selected row has changable === false
      if (!this.isClinic && this.selectedItems.some(item => !item.changable)) {
        notify({ type: 'error', message: 'Some tests you selected are not changeable. Please de-select them.' });
        return;
      }
      this.batchEditDialogVisible = true;
    },

    handleBatchUpdateTableRows(params) {
      const { products, affiliateIds, markUpType, markUpValue, changable } = params;

      products.forEach(product => {
        const index = this.productsData.findIndex(item => item.id === product.id && item.type === product.type);
        if (index === -1) return;

        const item = this.productsData[index];
        item.markUpType = markUpType;
        item.markUpValue = markUpValue;
        item.changable = changable;

        const wholesale = item.wholeSalePrice || item.basePrice;
        let markedUpPrice = wholesale;
        switch (markUpType) {
          case 'no_markup': markedUpPrice = wholesale; break;
          case 'added_percentage': markedUpPrice = wholesale * (1 + markUpValue); break;
          case 'fixed_profit': markedUpPrice = wholesale + markUpValue; break;
          case 'fixed_price': markedUpPrice = markUpValue; break;
        }

        item.markedUpPrice = markedUpPrice;
        item.retailPrice = markedUpPrice;
        item.profit = markedUpPrice - wholesale;
        item.revenue = item.profit;
        item.markup = this.getMarkupDisplay(item);
        item.selected = false;

        // Update cached markup schemes
        affiliateIds.forEach(affId => {
          if (this.cachedMarkupSchemes[affId]) {
            const itemType = this.getItemType(product.type);
            const ids = Array.isArray(product.ids) && product.ids.length ? product.ids : [product.id];
            ids.forEach(idVal => {
              const schemeKey = `${itemType}-${idVal}`;
              const existingIndex = this.cachedMarkupSchemes[affId].findIndex(
                s => `${s.item_type}-${s.item_id}` === schemeKey
              );
              const newScheme = {
                item_type: itemType,
                item_id: String(idVal),
                markup_type: markUpType,
                markup_value: markUpValue,
                changeable: changable,
                customized_name: null,
              };
              if (existingIndex > -1) {
                this.cachedMarkupSchemes[affId][existingIndex] = newScheme;
              } else {
                this.cachedMarkupSchemes[affId].push(newScheme);
              }
            });
          }
        });
      });

      console.log('Batch updated:', products.length, 'products');
    },

    handleDelete(item) {
      const index = this.productsData.findIndex(i => i.id === item.id);
      if (index > -1) {
        this.productsData.splice(index, 1);
      }
    },

    saveEdit() {
      const index = this.productsData.findIndex(i => i.id === this.editingItem.id);
      if (index > -1) {
        this.productsData[index] = { ...this.editingItem };
      }
      this.editDialogVisible = false;
    },

    // Handle update from EditRetailDetailsDialog (same logic as VA Portal's updateTableRows)
    handleUpdateTableRow(params) {
      const { product, affiliateIds, markUpType, markUpValue, customizedName, changable } = params;
      
      // Find and update the product in productsData
      const index = this.productsData.findIndex(item => item.id === product.id && item.type === product.type);
      if (index === -1) return;

      const item = this.productsData[index];
      
      // Update markup fields
      item.markUpType = markUpType;
      item.markUpValue = markUpValue;
      item.changable = changable;
      if (customizedName) {
        item.customized_name = customizedName;
      }

      // Recalculate prices (same logic as VA Portal)
      const wholesale = item.wholeSalePrice || item.basePrice;
      let markedUpPrice = wholesale;
      
      switch (markUpType) {
        case 'no_markup':
          markedUpPrice = wholesale;
          break;
        case 'added_percentage':
          markedUpPrice = wholesale * (1 + markUpValue);
          break;
        case 'fixed_profit':
          markedUpPrice = wholesale + markUpValue;
          break;
        case 'fixed_price':
          markedUpPrice = markUpValue;
          break;
      }

      item.markedUpPrice = markedUpPrice;
      item.retailPrice = markedUpPrice;
      item.profit = markedUpPrice - wholesale;
      item.revenue = item.profit;
      item.markup = this.getMarkupDisplay(item);

      // Update cached markup schemes for the affected affiliates
      // Expand ids array for VW deduped items (same as VA Portal formatDataForPostApi)
      const itemType = this.getItemType(product.type);
      const ids = Array.isArray(product.ids) && product.ids.length ? product.ids : [product.id];
      affiliateIds.forEach(affId => {
        if (this.cachedMarkupSchemes[affId]) {
          ids.forEach(idVal => {
            const schemeKey = `${itemType}-${idVal}`;
            const existingIndex = this.cachedMarkupSchemes[affId].findIndex(
              s => `${s.item_type}-${s.item_id}` === schemeKey
            );
            
            const newScheme = {
              item_type: itemType,
              item_id: String(idVal),
              markup_type: markUpType,
              markup_value: markUpValue,
              changeable: changable,
              customized_name: customizedName || null,
            };

            if (existingIndex > -1) {
              this.cachedMarkupSchemes[affId][existingIndex] = newScheme;
            } else {
              this.cachedMarkupSchemes[affId].push(newScheme);
            }
          });
        }
      });

      console.log('Product updated:', item.name, 'New retail price:', item.retailPrice);
    },

    // Get item_type for API (same as VA Portal)
    getItemType(productType) {
      switch (productType) {
        case 'va':
        case 'vw':
          return 'packagePriceId';
        case 'promotion':
          return 'oldPromotionId';
        case 'favorites':
          return 'shortcutId';
        case 'discount':
        case 'custom':
        default:
          return 'bundleId';
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },

    handleEditFee(fee) {
      // TODO: Implement edit fee dialog
      console.log('Edit fee:', fee);
    },

    handleEditBundle(bundle) {
      // TODO: Implement edit bundle dialog
      console.log('Edit bundle:', bundle);
      notify({ type: 'info', message: `Edit "${bundle.name}" - Feature coming soon` });
    },

    // Programs tab handlers
    async handleAddProgram() {
      try {
        // Import API function
        const { createWorkflowApi } = await import('@/api/workflowManage');

        // Create a new program with required program fields
        const response = await createWorkflowApi({
          name: 'Untitled Program ' + Math.floor(Math.random() * 10000000),
          description: '',
          type: 'patient',
          is_program: true,
          duration: {
            type: 'rolling',
            unit: 'days',
            value: 30
          },
          price: 0,
          definition: {
            nodes: [],
            connections: [],
            metadata: {}
          }
        });

        const newProgram = response.data;

        if (!newProgram || !newProgram.id) {
          throw new Error('Invalid program response from API');
        }

        // Navigate to workflow canvas with the new program ID
        this.$router.push(`/my-workbench/automation/edit/${newProgram.id}`);

        notify({
          title: 'Success',
          message: 'New program created successfully!',
          type: 'success',
          showClose: true
        });
      } catch (error) {
        console.error('Error creating draft program:', error);
        notify({
          title: 'Error',
          message: `Failed to create program: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },

    handleEditPriceBatch() {
      // TODO: Implement batch price edit for programs
      notify({ type: 'info', message: 'Batch price edit - Feature coming soon' });
    },

    handleViewProgram(programId) {
      // Navigate to view/edit program
      this.$router.push(`/my-workbench/automation/edit/${programId}`);
    },

    handleEditProgramPrice(programId) {
      // TODO: Implement single program price edit
      notify({ type: 'info', message: 'Edit program price - Feature coming soon' });
    },

    handleArchiveProgram(programId) {
      // TODO: Implement archive confirmation
      notify({ type: 'info', message: 'Archive program - Feature coming soon' });
    },
  },
};
</script>

<style scoped>
.ez-bill-page {
  padding: 0;
  background: transparent;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: transparent;
}

.page-title-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.page-title {
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  margin: 0;
  white-space: nowrap;
}

.page-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #666;
  margin: 0;
}

/* Content Area */
.content-area {
  background: #FFFFFF;
  margin: 0;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

/* Search Container */
.search-container {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  max-width: 1100px;
}

.search-label {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
}

.search-label-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.search-input-wrapper {
  flex: 1;
  min-width: 0;
}

/* Create Button */
.create-btn {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 24px;
  background: #004879;
  border: none;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.create-btn span {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #FFFFFF;
  white-space: nowrap;
}

.create-btn:hover {
  background: #003366;
  box-shadow: 0px 2px 6px 0px rgba(74, 74, 74, 0.35);
}

.create-btn:active {
  transform: translateY(1px);
}

/* Content Body */
.content-body {
  padding: 0 12px;
  flex: 1;
  overflow-y: auto;
}

/* Coming Soon State */
.coming-soon-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 20px;
  text-align: center;
}

.coming-soon-state p {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #7C8DA1;
  margin: 0;
}

/* Vibrant Products Content */
.vibrant-products-content {
  padding: 20px 0;
  flex: 1;
  position: relative;
  min-height: 500px;
}

.products-content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

/* Filter Row */
.filter-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 160px;
  min-width: 0;
  max-width: 250px;
}

.filter-label {
  overflow: hidden;
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

/* Batch Edit Button - push to the right of the filter row */
.batch-edit-btn-wrapper {
  margin-left: auto;
}

/* Main Tabs (Tab component, outlined) — color the selected icon to #004879 (brand blue).
   SVG file fill is #666 (unselected color); filter chain below recolors selected icon. */
.ez-bill-main-tab :deep(.va-tab--selected .va-tab__icon-img) {
  filter: brightness(0) saturate(100%) invert(15%) sepia(63%) saturate(3180%) hue-rotate(193deg) brightness(95%) contrast(101%);
}

/* Sub Tabs (Tab component, underline) — preserve original "tabs share full width" layout */
.ez-bill-sub-tab {
  margin-bottom: 16px;
  border-top: 1px solid #E5E7EB;
}

.ez-bill-sub-tab :deep(.va-tab-group__main) {
  width: 100%;
}

.ez-bill-sub-tab :deep(.va-tab) {
  flex: 1;
  justify-content: center;
  padding: 10px 16px;
  gap: 8px;
}

.ez-bill-sub-tab :deep(.va-tab__icon) {
  width: 24px;
  height: 24px;
}

/* Products Table */
.products-table-wrapper {
  width: 100%;
}

.products-table {
  border-radius: 4px;
}

.ez-bill-pagination {
  margin-top: 16px;
}

.table-header {
  display: flex;
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
}

.table-header .th {
  padding: 8px 12px;
  color: var(--IconGrey, #374957);
  text-align: center;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
  display: flex;
  align-items: center;
}

.table-header .th.checkbox {
  width: 50px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  justify-content: center;
  position: relative;
}

.table-header .th.checkbox :deep(.checkbox-container) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.table-header .th.test-name {
  flex: 2;
  min-width: 200px;
  text-align: left;
}

.table-header .th.bundle-name {
  flex: 2.5;
  min-width: 250px;
  text-align: left;
}

.table-header .th.price,
.table-header .th.markup,
.table-header .th.revenue,
.table-header .th.actions {
  flex: 1;
  text-align: center;
  min-width: 100px;
  justify-content: center;
}

/* Actions button styles */
.table-row .td.actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.table-row .td.actions .edit-btn,
.table-row .td.actions .delete-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 15px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.table-row .td.actions .edit-btn:hover {
  background-color: rgba(55, 73, 87, 0.1);
}

.table-row .td.actions .delete-btn:hover {
  background-color: #FEEBEB;
}

.table-body {
  min-height: 300px;
  max-height: 450px;
  overflow-y: auto;
}

.table-body::-webkit-scrollbar {
  width: 6px;
}

.table-body::-webkit-scrollbar-track {
  background: transparent;
}

.table-body::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.table-body::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 250px;
  color: #999;
  font-size: 16px;
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  min-height: 250px;
  color: #999;
  font-size: 16px;
  text-align: center;
}

.no-data-state p {
  margin: 0;
  line-height: 1.5;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #E5E7EB;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #F9FAFB;
}

/* Clickable row styles */
.table-row.clickable {
  cursor: pointer;
}

.table-row .td {
  padding: 8px 12px;
  font-size: 16px;
  color: var(--TrueBlack, #333);
  display: flex;
  align-items: center;
}

.table-row .td.checkbox {
  width: 50px;
  flex-shrink: 0;
  padding-left: 12px;
  padding-right: 12px;
  justify-content: center;
  position: relative;
}

/* Pin checkbox so its 28→33px hover-grow doesn't expand the row height */
.table-row .td.checkbox :deep(.checkbox-container) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.table-row .td.test-name {
  flex: 2;
  min-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-row .td.bundle-name {
  flex: 2.5;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
}

.bundle-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bundle-name-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #111827;
}

.test-count {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #666;
  background: #F3F4F6;
  padding: 2px 8px;
  border-radius: 12px;
}

.table-row .td.price,
.table-row .td.markup,
.table-row .td.revenue,
.table-row .td.actions {
  flex: 1;
  text-align: center;
  min-width: 100px;
  justify-content: center;
}

.table-row .td.actions {
  gap: 16px;
}

/* Edit dialog form styles */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.edit-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.edit-form .form-group label {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.edit-form .form-group .form-input {
  padding: 8px 12px;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.edit-form .form-group .form-input:focus {
  border-color: #004879;
}
</style>

<style>
/* SingleSelector styles - same as Medication Dialog */
</style>
